import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
var _excluded = ["wrap", "children"],
  _excluded2 = ["iconOnly", "dimmed", "as", "background", "children", "deleteLabel", "forwardedAs", "icon", "iconColor", "iconRenderer", "id", "onDelete"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.array.join.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.string.includes.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import useUniqueId from 'hooks/useUniqueId';
import { useTranslation } from 'react-i18next';
import { pxToRem, alpha, getBackgroundShade, getColorBlock, contrast, createStyleFunction, getPropFilter, getPropNamesFromPropDefinition, _3px } from 'utils/ui';
import { fillParent, borderValue } from 'styles/mixins';
import { positionPropsDef, marginPropsDef } from 'styles/styleProps';
import Box from 'components/Box';
import ButtonCore from 'components/ButtonCore';
import Button from 'components/Button';
import Text from 'components/Text';
import Icon from 'components/Icon';
import ThemeSection from 'components/ThemeSection';
import VisuallyHidden from 'components/VisuallyHidden';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var stylePropDefs = [].concat(_toConsumableArray(positionPropsDef), _toConsumableArray(marginPropsDef));
var styleProps = createStyleFunction(stylePropDefs);
var stylePropNames = getPropNamesFromPropDefinition(stylePropDefs);
var pillHeight = pxToRem(24);
var iconSpacing = pxToRem(30);
var customWrapperProps = [].concat(_toConsumableArray(stylePropNames), ['background', 'dimmed', 'isClickable', 'hasIcon', 'hasSideButton', 'iconOnly']);
var Wrapper = styled.div.withConfig({
  shouldForwardProp: getPropFilter(customWrapperProps)
}).withConfig({
  displayName: "Pill__Wrapper",
  componentId: "sc-avqcyi-0"
})(["position:relative;", " max-width:100%;min-width:0;", " padding:0 ", ";", " font-size:", ";font-weight:600;line-height:", ";color:", ";background-color:", ";transition:background-color 250ms linear;border-radius:1em;", " ", ";", ";&.focus-visible{outline:none;}"], function (p) {
  return p.hasSideButton ? "\n\t\tflex-shrink: 1;\n\t" : "\n\t\tdisplay: inline-block;\n\t\tvertical-align: top;\n\t";
}, styleProps, function (p) {
  return p.iconOnly ? 0 : p.theme.globals.spacing.xs;
}, function (p) {
  return p.hasIcon && !p.iconOnly && "\n\t\t\tpadding-left: ".concat(iconSpacing, ";\n\t\t");
}, function (p) {
  return p.theme.globals.typeScale.xs;
}, pillHeight, function (p) {
  return p.background ? contrast(getColorBlock(p.background, p.theme)) : p.theme.text;
}, function (p) {
  return p.background ? getColorBlock(p.background, p.theme) : getBackgroundShade(p.theme);
}, function (p) {
  return p.hasSideButton && "\n\t\t\tborder-top-right-radius: 0;\n\t\t\tborder-bottom-right-radius: 0;\n\t\t\tborder-right: ".concat(p.iconOnly ? 0 : borderValue(p.theme), ";\n\t\t");
}, function (p) {
  return p.dimmed && "\n\t\t\topacity: ".concat(p.theme.textDimStrength, ";\n\t\t");
}, function (p) {
  return p.isClickable && "\n\t\t\tcursor: pointer;\n\t\t";
});
var HoverShade = styled.span.withConfig({
  displayName: "Pill__HoverShade",
  componentId: "sc-avqcyi-1"
})(["", " border-radius:inherit;background-color:", ";opacity:0;transition:opacity 250ms linear;will-change:opacity;", ":not(.is-disabled):hover > &&{opacity:1;transition-duration:50ms;}", ":not(.is-disabled):active > &&{opacity:0;transition-duration:250ms;}"], fillParent, function (p) {
  return alpha(p.theme.shade, Math.min(p.theme.shadeStrength, 0.12));
}, Wrapper, Wrapper);
var FocusRing = styled.span.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return prop !== 'color';
  }
}).withConfig({
  displayName: "Pill__FocusRing",
  componentId: "sc-avqcyi-2"
})(["", " border-radius:inherit;box-shadow:0 0 0 ", " ", ";opacity:0;transition:opacity 250ms linear;will-change:opacity;", ".focus-visible > &&{top:-", ";left:-", ";bottom:-", ";right:-", ";box-shadow:0 0 0 ", " ", ";opacity:1;transition-duration:50ms;}", ":not(.is-disabled):active > &&{opacity:1;transition-duration:50ms;}"], fillParent, _3px, function (p) {
  return alpha(p.theme.shade, p.theme.lineStrength + 0.05);
}, Wrapper, _3px, _3px, _3px, _3px, _3px, function (p) {
  return p.theme.links;
}, Wrapper);
var IconWrapper = styled.span.withConfig({
  displayName: "Pill__IconWrapper",
  componentId: "sc-avqcyi-3"
})(["display:flex;align-items:center;justify-content:center;width:", ";height:", ";font-size:0.9em;color:", ";background-color:", ";border-radius:100%;"], pillHeight, pillHeight, function (p) {
  return p.theme.text;
}, function (p) {
  return p.theme.background;
});
var SideButton = styled(Button).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return prop !== 'background';
  }
}).withConfig({
  displayName: "Pill__SideButton",
  componentId: "sc-avqcyi-4"
})(["height:", ";padding:", ";border-radius:0 1em 1em 0;color:", ";background-color:", ";"], pillHeight, pxToRem(5), function (p) {
  return p.background ? contrast(getColorBlock(p.background, p.theme)) : p.theme.text;
}, function (p) {
  return p.background ? getColorBlock(p.background, p.theme) : getBackgroundShade(p.theme);
});
function ConditionalFlexWrapper(_ref) {
  var wrap = _ref.wrap,
    children = _ref.children,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  if (!wrap) {
    return children;
  }
  return /*#__PURE__*/_jsx(Box, _objectSpread(_objectSpread({}, otherProps), {}, {
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "100%",
    children: children
  }));
}
function defaultIconRenderer(_ref2) {
  var iconName = _ref2.iconName,
    iconColor = _ref2.iconColor;
  if (!iconName) {
    return null;
  }
  return /*#__PURE__*/_jsx(ThemeSection, {
    name: iconColor || iconName,
    children: /*#__PURE__*/_jsx(IconWrapper, {
      children: /*#__PURE__*/_jsx(Icon, {
        name: iconName,
        "data-iconname": iconName
      })
    })
  });
}
function splitStyleProps(props) {
  var styleProps = {};
  var filteredProps = {};
  for (var propName in props) {
    if (stylePropNames.includes(propName)) {
      styleProps[propName] = props[propName];
    } else {
      filteredProps[propName] = props[propName];
    }
  }
  return {
    styleProps: styleProps,
    filteredProps: filteredProps
  };
}

// NOTE: <Pill /> component does not resize on mobile view

export var Pill = /*#__PURE__*/forwardRef(function (_ref3, ref) {
  var _ref3$iconOnly = _ref3.iconOnly,
    iconOnly = _ref3$iconOnly === void 0 ? false : _ref3$iconOnly,
    dimmed = _ref3.dimmed,
    as = _ref3.as,
    background = _ref3.background,
    children = _ref3.children,
    deleteLabel = _ref3.deleteLabel,
    forwardedAs = _ref3.forwardedAs,
    icon = _ref3.icon,
    iconColor = _ref3.iconColor,
    _ref3$iconRenderer = _ref3.iconRenderer,
    iconRenderer = _ref3$iconRenderer === void 0 ? defaultIconRenderer : _ref3$iconRenderer,
    id = _ref3.id,
    onDelete = _ref3.onDelete,
    otherProps = _objectWithoutProperties(_ref3, _excluded2);
  var uniqueId = useUniqueId(id);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var wrapperId = "".concat(uniqueId, "_wrapper");
  var sideButtonId = "".concat(uniqueId, "_side_button");
  var isClickable = otherProps.onClick || otherProps.to || otherProps.href || otherProps.isClickable;
  var hasSideButton = Boolean(onDelete);
  var token = iconRenderer({
    iconName: icon,
    iconColor: iconColor
  });
  var _splitStyleProps = splitStyleProps(otherProps),
    styleProps = _splitStyleProps.styleProps,
    filteredProps = _splitStyleProps.filteredProps;
  return /*#__PURE__*/_jsx(ConditionalFlexWrapper, _objectSpread(_objectSpread({
    wrap: hasSideButton
  }, styleProps), {}, {
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Wrapper, _objectSpread(_objectSpread({}, hasSideButton ? filteredProps : otherProps), {}, {
        ref: ref,
        as: isClickable ? ButtonCore : as,
        forwardedAs: isClickable ? as : forwardedAs,
        id: wrapperId,
        isClickable: isClickable,
        dimmed: dimmed,
        hasIcon: Boolean(token),
        hasSideButton: hasSideButton,
        background: background,
        iconOnly: iconOnly,
        children: [isClickable && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(HoverShade, {}), /*#__PURE__*/_jsx(FocusRing, {})]
        }), token && (iconOnly ? token : /*#__PURE__*/_jsx(Box, {
          top: 0,
          left: 0,
          position: "absolute",
          children: token
        })), iconOnly ? /*#__PURE__*/_jsxs(VisuallyHidden, {
          children: [children, ' ', dimmed && t('state_greyed_out_brackets')]
        }) : /*#__PURE__*/_jsxs(Text, {
          display: "block",
          overflow: "ellipsis",
          children: [children, dimmed && /*#__PURE__*/_jsxs(VisuallyHidden, {
            children: [' ', t('state_greyed_out_brackets')]
          })]
        })]
      })), hasSideButton && /*#__PURE__*/_jsx(SideButton, {
        iconOnly: true,
        round: true,
        id: sideButtonId,
        icon: "x",
        size: "small",
        variant: "shaded",
        background: background,
        onClick: onDelete,
        "aria-labelledby": [sideButtonId, wrapperId].join(' '),
        children: deleteLabel
      })]
    })
  }));
});
Pill.displayName = 'Pill';
export default Pill;